import React, { useState, useEffect, useRef } from 'react';
import * as faceapi from 'face-api.js';
import api from '../services/api';
import toast, { Toaster } from 'react-hot-toast';

const AttendanceKiosk = () => {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [cedula, setCedula] = useState('');
  const [step, setStep] = useState('input'); // input, camera, success
  const [summary, setSummary] = useState(null);
  const [message, setMessage] = useState('');
  const videoRef = useRef();
  const canvasRef = useRef();

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    try {
      const MODEL_URL = '/models';
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
      ]);
      setLoading(false);
    } catch (error) {
      console.error("Error loading models:", error);
      toast.error("Error al cargar el sistema de reconocimiento");
    }
  };

  const startVideo = () => {
    setStep('camera');
    navigator.mediaDevices.getUserMedia({ video: {} })
      .then(stream => {
        videoRef.current.srcObject = stream;
      })
      .catch(err => console.error(err));
  };

  const stopVideo = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
    }
  };

  const handleNext = () => {
    if (!cedula) {
      toast.error("Por favor ingresa tu número de cédula");
      return;
    }
    startVideo();
  };

  const captureAndSubmit = async () => {
    if (processing) return;
    setProcessing(true);

    try {
      const detections = await faceapi.detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions());
      
      if (!detections) {
        toast.error("No se detecta rostro. Por favor mira a la cámara.");
        setProcessing(false);
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      
      // Convertir canvas a Blob para envío como archivo
      const photoBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.8));
      
      // Primero obtener el employee_id por la cédula
      const empResponse = await api.get(`/attendance/employee/${cedula}`);
      if (!empResponse.data.success) {
        toast.error("Cédula no encontrada");
        setProcessing(false);
        return;
      }
      
      const employee = empResponse.data.data;
      
      // Determinar el siguiente evento (entrada/salida)
      const statusRes = await api.get(`/attendance/employee-status/${employee.id}`);
      const nextEvent = statusRes.data.data.nextEvent;
      
      if (!nextEvent) {
        toast.error("Ya has completado todos los registros de hoy");
        setProcessing(false);
        return;
      }

      const formData = new FormData();
      formData.append('employee_id', employee.id);
      formData.append('type', nextEvent);
      formData.append('photo', photoBlob, 'attendance.jpg');

      const response = await api.post('/attendance/record', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const type = nextEvent;
        // Obtener logs actualizados para el resumen
        const updatedStatus = await api.get(`/attendance/employee-status/${employee.id}`);
        const logs = updatedStatus.data.data.events;
        
        setSummary({ type, employee, logs });
        
        if (type === 'entry') {
          setMessage(`¡Bienvenido(a), ${employee.full_name.split(' ')[0]}! Qué gusto tenerte aquí. Que tengas una jornada productiva y llena de éxitos.`);
        } else if (type === 'exit') {
          setMessage(`Gracias por hacer parte de este equipo, ${employee.full_name.split(' ')[0]}. Te deseamos un feliz resto de día y un buen descanso.`);
        } else {
          setMessage(`Registro de ${type === 'lunch_out' ? 'salida a almuerzo' : 'regreso de almuerzo'} exitoso.`);
        }
        
        setStep('success');
        stopVideo();
        
        setTimeout(() => {
          setStep('input');
          setCedula('');
          setSummary(null);
        }, 10000);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Error al registrar asistencia");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-slate-400 font-medium italic">Iniciando sistema de asistencia...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <Toaster position="top-center" />
      
      <div className="w-full max-w-4xl bg-slate-900 rounded-3xl shadow-2xl overflow-hidden border border-slate-800">
        <div className="p-8 md:p-12">
          {step === 'input' && (
            <div className="max-w-md mx-auto text-center space-y-8 animate-fade-in">
              <div className="space-y-2">
                <h1 className="text-4xl font-black text-white">Topping Frozen</h1>
                <p className="text-slate-400 text-lg">Control de Asistencia</p>
              </div>

              <div className="space-y-4">
                <label className="block text-left text-sm font-bold text-slate-500 uppercase tracking-widest ml-1">Número de Cédula</label>
                <input
                  type="text"
                  value={cedula}
                  onChange={(e) => setCedula(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleNext()}
                  className="w-full bg-slate-800 border-2 border-slate-700 rounded-2xl p-6 text-3xl font-bold text-white text-center focus:border-blue-500 focus:outline-none transition-all placeholder-slate-600 shadow-inner"
                  placeholder="12345678"
                  autoFocus
                />
                <button
                  onClick={handleNext}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white font-black py-6 rounded-2xl text-xl shadow-lg shadow-blue-900/20 transition-all transform active:scale-95"
                >
                  SIGUIENTE
                </button>
              </div>
              
              <p className="text-slate-500 text-sm">
                Tu asistencia es fundamental para nuestro crecimiento.
              </p>
            </div>
          )}

          {step === 'camera' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center animate-fade-in">
              <div className="relative rounded-3xl overflow-hidden border-4 border-slate-800 bg-black aspect-square shadow-2xl">
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 border-[40px] border-black/20 pointer-events-none"></div>
                <div className="absolute top-4 left-4 bg-red-600 text-white text-[10px] font-black px-2 py-1 rounded flex items-center gap-1 uppercase tracking-tighter">
                  <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                  Cámara en vivo
                </div>
              </div>

              <div className="space-y-8 text-center md:text-left">
                <div className="space-y-2">
                  <h2 className="text-3xl font-black text-white uppercase tracking-tight">Verificación Biométrica</h2>
                  <p className="text-slate-400">Por favor, mira fijamente a la cámara y asegúrate de estar en un lugar iluminado.</p>
                </div>

                <button
                  onClick={captureAndSubmit}
                  disabled={processing}
                  className={`w-full ${processing ? 'bg-slate-700' : 'bg-green-600 hover:bg-green-500'} text-white font-black py-8 rounded-3xl text-2xl shadow-xl shadow-green-900/20 transition-all flex items-center justify-center gap-4`}
                >
                  {processing ? (
                    <>
                      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-white"></div>
                      PROCESANDO...
                    </>
                  ) : (
                    'REGISTRAR AHORA'
                  )}
                </button>
                
                <button 
                  onClick={() => { stopVideo(); setStep('input'); }}
                  className="w-full text-slate-500 font-bold hover:text-slate-300 transition-colors"
                >
                  VOLVER
                </button>
              </div>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center space-y-8 animate-bounce-in">
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto shadow-lg shadow-green-900/40">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>

              <div className="space-y-4">
                <h2 className="text-4xl font-black text-white tracking-tight leading-none uppercase">
                  Registro Exitoso
                </h2>
                <p className="text-2xl text-blue-400 font-medium max-w-2xl mx-auto leading-tight italic">
                  "{message}"
                </p>
              </div>

              {summary && (
                <div className="bg-slate-800/50 rounded-3xl p-8 max-w-2xl mx-auto border border-slate-700 shadow-inner">
                  <h3 className="text-slate-500 text-xs font-black uppercase tracking-[0.2em] mb-6 text-center">Registros de hoy</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {['entry', 'lunch_out', 'lunch_in', 'exit'].map(type => {
                      const log = summary.logs?.find(l => l.type === type);
                      const labels = {
                        entry: 'Ingreso',
                        lunch_out: 'Almuerzo (Salida)',
                        lunch_in: 'Almuerzo (Regreso)',
                        exit: 'Salida Final'
                      };
                      return (
                        <div key={type} className={`text-center p-4 rounded-2xl border ${log ? 'bg-slate-900/50 border-blue-900/30' : 'bg-transparent border-slate-800 opacity-30'}`}>
                          <p className="text-slate-500 text-[9px] uppercase font-black mb-2 tracking-tighter">{labels[type]}</p>
                          <p className={`text-xl font-black ${log ? 'text-white' : 'text-slate-700'}`}>
                            {log ? new Date(log.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: true}) : '--:--'}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="pt-8">
                <button 
                  onClick={() => { setStep('input'); setCedula(''); }}
                  className="px-8 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-full text-sm font-black uppercase tracking-widest transition-all"
                >
                  Cerrar Ventana
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceKiosk;
